import { parseKeyValueLines } from "./key-value-lines";
import type { PrismicCollectionDetailField } from "types";

export const METADATA_QR_PREFIX = "dc-meta:";
const reserved = new Set([
  "__proto__",
  "constructor",
  "prototype",
  "user_id",
  "session_id",
  "task_id",
  "collection_id",
  "capture_id",
  "step_label",
]);

export function isMetadataFieldId(id: string) {
  return /^[A-Za-z][A-Za-z0-9_-]*$/.test(id) && !reserved.has(id);
}

export interface MetadataField {
  id: string;
  title: string;
  type: string;
  required?: boolean;
  qrEnabled?: boolean;
  options?: Array<{ key: string; value: string }>;
  dropdownValueSide?: "key" | "value";
}

export function metadataFields(
  fields: PrismicCollectionDetailField[]
): MetadataField[] {
  return fields
    .filter((field) => !field.use_as_step_labels)
    .map((field) => ({
      id: String(field.label_id || "").trim(),
      title: String(field.label_title || field.label_id || "Field"),
      type: String(field.label_type || "text"),
      required: Boolean(field.is_required),
      qrEnabled: field.qr_prefill === true,
      options: parseKeyValueLines(field.dropdown_options),
    }));
}

export function normalizeMetadataValue(
  field: MetadataField,
  input: unknown
): string {
  if (typeof input !== "string" && typeof input !== "number")
    throw new Error(`${field.title} must contain one text or numeric value.`);
  const value = String(input).trim();
  if (
    !value ||
    value.length > 256 ||
    /[,:|]/.test(value) ||
    Array.from(value).some((character) => character.charCodeAt(0) < 32)
  )
    throw new Error(
      `${field.title} is empty, too long, or contains an unsupported separator.`
    );
  if (field.type === "numeric" && !Number.isFinite(Number(value)))
    throw new Error(`${field.title} must be a number.`);
  if (field.type === "date") {
    const date = new Date(value);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
      Number.isNaN(date.getTime()) ||
      date.toISOString().slice(0, 10) !== value
    )
      throw new Error(`${field.title} must be a valid date (YYYY-MM-DD).`);
  }
  if (field.type === "dropdown") {
    const option = field.options?.find((option) => option.value === value);
    if (!option)
      throw new Error(
        `${field.title} is not an available option for this collection.`
      );
    // Preserve the existing phone/RTMS key-based form contract. QR inputs always carry codes.
    return option[field.dropdownValueSide || "value"];
  }
  if (!["text", "numeric", "date"].includes(field.type))
    throw new Error(`${field.title} does not support QR prefilling.`);
  return value;
}

export function readMetadataQr(
  raw: string,
  fields: MetadataField[],
  collectionId?: string
) {
  if (raw.length > 8192 || !raw.startsWith(METADATA_QR_PREFIX))
    throw new Error(
      "Scan a collection-details QR code, rather than a capture or website QR."
    );
  let payload;
  try {
    payload = JSON.parse(raw.slice(METADATA_QR_PREFIX.length));
  } catch {
    throw new Error("This collection-details QR code is not valid JSON.");
  }
  if (
    !payload ||
    payload.version !== 1 ||
    !payload.labels ||
    typeof payload.labels !== "object" ||
    Array.isArray(payload.labels)
  )
    throw new Error(
      "This collection-details QR version or label format is not supported."
    );
  if (
    payload.collection_id !== undefined &&
    (typeof payload.collection_id !== "string" ||
      payload.collection_id !== collectionId)
  )
    throw new Error("This QR code belongs to a different collection.");
  const entries = Object.entries(payload.labels);
  if (!entries.length || entries.length > 50)
    throw new Error("The QR must contain between 1 and 50 details.");
  const allowed = fields.filter((field) => field.qrEnabled);
  const ids = allowed.map((field) => field.id);
  if (new Set(ids).size !== ids.length)
    throw new Error("QR-enabled fields must have unique label IDs.");
  const changes = entries.flatMap(([id, value]) => {
    if (!isMetadataFieldId(id))
      throw new Error(
        "The QR contains a reserved or invalid metadata field ID."
      );
    const field = allowed.find((field) => field.id === id);
    return field
      ? [{ field, value: normalizeMetadataValue(field, value) }]
      : [];
  });
  if (!changes.length)
    throw new Error("This QR has no fields enabled for scanning in this form.");
  return { changes, ignored: entries.length - changes.length };
}

export function metadataDisplayValue(
  field: MetadataField,
  value: unknown
): string {
  return (
    field.options?.find(
      (option) => option[field.dropdownValueSide || "value"] === value
    )?.key || String(value || "")
  );
}
